import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchLandListings } from '../../store/slices/landListingsSlice';
import { landListingsAPI } from '../../services/landListingsAPI';
import { getPhoneAuthToken } from '../../services/auth';
import LandCard from '../common/LandCard';
import { useLanguage } from '../../contexts/LanguageContext';

const DesktopLandCarousel = lazy(() => import('./DesktopLandCarousel'));
const PhoneAuthModal = lazy(() => import('../PhoneAuthModal'));

const LandSection = ({ title, lands, sectionId, isDesktop, likedIds, onToggleLike, onCardClick, priorityCount = 0 }) => {
  const isLiked = (id) => likedIds.has(id);

  if (!lands || lands.length === 0) {
    return null;
  }

  return (
    <div className="mb-8 md:mb-10 pt-6">
      {/* Section Header — Airbnb-style compact heading */}
      <div className="flex items-center justify-between mb-4 px-1 pt-2">
        <h3 className="text-lg md:text-xl font-semibold text-gray-900 tracking-tight font-montserrat">{title}</h3>
      </div>

      {isDesktop ? (
        <Suspense fallback={<div className="h-[430px]" aria-hidden="true" />}>
          <DesktopLandCarousel
            lands={lands}
            sectionId={sectionId}
            likedIds={likedIds}
            onToggleLike={onToggleLike}
            onCardClick={onCardClick}
            priorityCount={priorityCount}
          />
        </Suspense>
      ) : (
        <div className="overflow-x-auto scrollbar-hide -mx-6">
          <div className="flex gap-4 pb-2 px-6">
            {lands.slice(0, 5).map((listing, index) => (
              <div key={listing.id || index} className="flex-shrink-0 w-[280px]">
                <LandCard
                  listing={listing}
                  liked={isLiked(listing.id)}
                  onToggleLike={() => onToggleLike(listing)}
                  onClick={() => onCardClick(listing)}
                  showPrice={false}
                  showStatus={false}
                  priority={index < Math.min(priorityCount, 2)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Skeleton placeholder for a single LandCard while data loads.
 */
const CardSkeleton = () => (
  <div className="bg-white rounded-lg overflow-hidden shadow-sm">
    <div className="aspect-[4/5] relative bg-gray-200 animate-pulse" />
    <div className="p-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
    </div>
  </div>
);

/**
 * Shows 5 skeleton cards in the same carousel layout as LandSection.
 */
const SkeletonSection = ({ title }) => (
  <div className="mb-8 md:mb-10 pt-6">
    <div className="flex items-center justify-between mb-4 px-1 pt-2">
      <h3 className="text-lg md:text-xl font-semibold text-gray-900 tracking-tight font-montserrat">{title}</h3>
    </div>
    {/* Mobile */}
    <div className="block md:hidden overflow-x-auto scrollbar-hide -mx-6">
      <div className="flex gap-4 pb-2 px-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex-shrink-0 w-[280px]"><CardSkeleton /></div>
        ))}
      </div>
    </div>
    {/* Desktop */}
    <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  </div>
);

/**
 * Hook: returns [ref, hasBeenVisible].
 * Fires once when the element enters the viewport (+ rootMargin).
 */
const useInView = (rootMargin = '200px') => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin]);
  return [ref, visible];
};

const LandSections = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { featuredListings, loading, error } = useSelector(state => state.landListings);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [likedIds, setLikedIds] = useState(new Set());
  const [authTargetId, setAuthTargetId] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  ));

  // Fetch on mount — use a higher limit to ensure all approved listings are returned
  useEffect(() => {
    dispatch(fetchLandListings({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    const initLikes = async () => {
      const token = getPhoneAuthToken();
      if (!token) {
        setLikedIds(new Set());
        return;
      }
      try {
        const res = await landListingsAPI.getUserLikes();
        setLikedIds(new Set((res?.data || []).map((l) => l.property_id || l.properties?.id).filter(Boolean)));
      } catch { }
    };
    initLikes();
  }, [isLoggedIn]);

  const toggleLike = async (listing) => {
    const id = listing?.id;
    if (!id) return;
    if (!getPhoneAuthToken()) {
      setAuthTargetId(id);
      return;
    }
    try {
      if (likedIds.has(id)) {
        await landListingsAPI.unlikeProperty(id);
        setLikedIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
      } else {
        await landListingsAPI.likeProperty(id);
        setLikedIds((current) => new Set(current).add(id));
      }
    } catch (e) {
      console.error('Failed to toggle like', e);
    }
  };

  const handleCardClick = (listing) => {
    landListingsAPI.trackPropertyView({
      property_id: listing.id,
      session_id: `sess_${Math.random().toString(36).slice(2, 10)}`,
      source: 'website',
    }).catch(() => {});
    navigate(`/land-details/${listing.id}`);
  };

  // Refs/visibility for lower sections — defer rendering until scrolled near
  const [topViewedRef, topViewedVisible] = useInView('300px');
  const [allLandsRef, allLandsVisible] = useInView('300px');

  // Filter out private lands
  const publicListings = useMemo(() => {
    return featuredListings.filter(listing => listing.status !== 'Private');
  }, [featuredListings]);

  // New Lands — sorted by created_at descending
  const newLands = useMemo(() => {
    return [...publicListings].sort((a, b) => {
      const dateA = new Date(a.created_at || a.createdAt || 0);
      const dateB = new Date(b.created_at || b.createdAt || 0);
      return dateB - dateA;
    });
  }, [publicListings]);

  // Top Viewed — sorted by updated_at (no per-listing API calls on homepage)
  const topViewedLands = useMemo(() => {
    return [...publicListings].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0);
      const dateB = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0);
      return dateB - dateA;
    });
  }, [publicListings]);

  // All Lands currently uses the same ordering as Top Viewed.
  const allLands = topViewedLands;

  const newLandsTitle = t('home.newLands') || 'New Lands';
  const topViewedTitle = t('home.topViewedLands') || 'Top Viewed Lands';
  const allLandsTitle = t('home.landsNearYou') || 'Lands Near You';

  return (
    <div className="px-4 md:px-6 py-8 md:py-12 bg-gradient-to-b from-white via-gray-50/30 to-white relative overflow-y-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-green/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* New Lands — skeleton while loading (unless cached cards exist), real cards when ready */}
        {loading && featuredListings.length === 0 ? (
          <SkeletonSection title={newLandsTitle} />
        ) : error && featuredListings.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">Error loading lands: {error}</p>
            <button
              onClick={() => dispatch(fetchLandListings({ limit: 100 }))}
              className="px-6 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green/90"
            >
              Retry
            </button>
          </div>
        ) : (
          <LandSection title={newLandsTitle} lands={newLands} sectionId="new-lands" priorityCount={4} isDesktop={isDesktop} likedIds={likedIds} onToggleLike={toggleLike} onCardClick={handleCardClick} />
        )}

        {/* Top Viewed — skeleton until near viewport */}
        <div ref={topViewedRef}>
          {!topViewedVisible || (loading && featuredListings.length === 0) ? (
            <SkeletonSection title={topViewedTitle} />
          ) : (
            topViewedLands.length > 0 && (
              <LandSection title={topViewedTitle} lands={topViewedLands} sectionId="top-viewed" isDesktop={isDesktop} likedIds={likedIds} onToggleLike={toggleLike} onCardClick={handleCardClick} />
            )
          )}
        </div>

        {/* All Lands — skeleton until near viewport */}
        <div ref={allLandsRef}>
          {!allLandsVisible || (loading && featuredListings.length === 0) ? (
            <SkeletonSection title={allLandsTitle} />
          ) : (
            <LandSection title={allLandsTitle} lands={allLands} sectionId="all-lands" isDesktop={isDesktop} likedIds={likedIds} onToggleLike={toggleLike} onCardClick={handleCardClick} />
          )}
        </div>

        {!loading && publicListings.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No lands available at the moment.</p>
          </div>
        )}
      </div>
      {authTargetId && (
        <Suspense fallback={null}>
          <PhoneAuthModal
            onClose={() => setAuthTargetId(null)}
            onSuccess={() => {
              const target = publicListings.find((l) => l.id === authTargetId);
              if (target) toggleLike(target);
            }}
            title="Login to like"
            subtitle="Enter your phone number to save properties"
          />
        </Suspense>
      )}
    </div>
  );
};

export default LandSections;
